"use client";
import { Contact_Us_FormDetails, getTimezone, GetCountry } from "@/api/Api";
import { Grid, InputLabel } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import styles from "./ContactUsForm.module.scss";
import {
  showToast,
  TOAST_TYPE,
  FORM_TOAST_CONTAINER_ID,
} from "@/components/Common/Toast";
import * as Yup from "yup";
import Form from "../Form/Form";
import TermsConditionBtn from "../TermsConditionBtn";
import Image from "next/image";
import { sendEventToGTM, getCDNImageUrl } from "@/utils/Common";
import {
  trackFormLoaded,
  trackFormSubmitted,
  FORM_TYPES,
  getPageType,
} from "@/utils/gtmEvents";

/**
 * App-wide form-toast container id. The single `<Toaster>` for it lives in
 * `components/Layout.jsx` — see `components/Common/Toast/constants.js` for why it
 * is mounted there and not per-form.
 */
const TOAST_CONTAINER_ID = FORM_TOAST_CONTAINER_ID;

const ContactUsForm = ({
  options = [],
  handleClose,
  isNorthAmericaAds = false,
  // Catalogue id of the product this enquiry came from, sent as `product` in the
  // payload.
  //
  // ⚠️ `ContactUsModalBtn` (components/Modal/ContactUs.jsx) has ALWAYS passed
  // `productId` down to this form — along with `service`, `submitButtonLabel`,
  // `submitButtonWidth`, `additionalInformationInitialValue`, `isUsVersion` and
  // `gtmSubmitEvent`. None of them were declared here, so every one was silently
  // dropped: JSX props for undeclared parameters vanish without a warning. That is
  // why "Request Quote" submitted with product null even from a product page.
  // Only `productId` is wired up here; the others remain unimplemented.
  productId = null,
}) => {
  const [contactUs, setContactUs] = useState({
    isChecked: false,
    isLoading: false,
    countryOptions: [],
    isSuccess: false,
  });
  const { isChecked, isLoading, countryOptions, isSuccess } = contactUs;
  const pathname = usePathname();

  const getCountries = async () => {
    try {
      const countryData = await GetCountry();
      setContactUs((prev) => {
        return { ...prev, countryOptions: countryData };
      });
    } catch (error) {
      showToast("Error fetching data", {
        type: TOAST_TYPE.ERROR,
        containerId: TOAST_CONTAINER_ID,
      });
    }
  };
  useEffect(() => {
    getCountries();

    // Track FORM_LOADED event
    const pageUrl = typeof window !== "undefined" ? window.location.href : "";
    const pageType = getPageType(pathname);
    trackFormLoaded(FORM_TYPES.CONTACT_US, pageUrl, pageType);
  }, [pathname]);

  const checkedPolicy = () => {
    setContactUs((prev) => {
      return { ...prev, isChecked: !prev.isChecked };
    });
  };

  const handleContactForm = async (values, resetForm) => {
    setContactUs((prev) => {
      return { ...prev, isLoading: true };
    });
    try {
      const res = await Contact_Us_FormDetails({
        ...values,
        recaptcha: values.recaptcha,
        call_time: values.call_time === "none" ? null : values.call_time,
        // Spread conditionally so callers that pass no productId send a body with
        // no `product` key at all, rather than `product: null`. `Contact_Us_FormDetails`
        // posts the payload straight through, so this is the only wiring needed.
        ...(productId ? { product: productId } : {}),
      });
      if (res.status == 200) {
        // Track FORM_SUBMITTED event
        const pageUrl = typeof window !== "undefined" ? window.location.href : "";
        const pageType = getPageType(pathname);
        trackFormSubmitted(FORM_TYPES.CONTACT_US, pageUrl, pageType);

        setContactUs((prev) => {
          return {
            ...prev,
            isChecked: false,
            isLoading: false,
            isSuccess: true,
          };
        });
        // Success feedback is EITHER the panel or a toast, never both:
        // - No `handleClose` (rendered as a page section): the `isSuccess` branch
        //   below swaps the form for the success panel, so a toast would repeat it.
        // - With `handleClose` (rendered in `components/Modal/ContactUs.jsx`): the
        //   modal closes on this same commit, so the panel is never seen and the
        //   toast is the only confirmation the user gets.
        if (handleClose) {
          showToast("Request Submitted, We will get back soon!", {
            type: TOAST_TYPE.SUCCESS,
            containerId: TOAST_CONTAINER_ID,
          });
        }
        window?.grecaptcha?.reset();
        window.scrollTo({ top: 0, behavior: "smooth" });
        resetForm("");
        if (handleClose) {
          handleClose();
        }
      } else {
        // A non-200 response used to fall through here silently, leaving
        // isLoading stuck true — which keeps the submit button disabled forever
        // with no message. Surface it and release the button instead.
        showToast("Something went wrong, please try again!", {
          type: TOAST_TYPE.ERROR,
          containerId: TOAST_CONTAINER_ID,
        });
        setContactUs((prev) => {
          return { ...prev, isLoading: false };
        });
      }
    } catch (error) {
      showToast("Something went wrong, please try again!", {
        type: TOAST_TYPE.ERROR,
        containerId: TOAST_CONTAINER_ID,
      });
      setContactUs((prev) => {
        return { ...prev, isLoading: false };
      });
    }
  };

  useEffect(() => {
    isSuccess &&
      setTimeout(() => {
        setContactUs((prev) => {
          return { ...prev, isSuccess: false };
        });
      }, 10000);
  }, [isSuccess]);

  return (
    <>
      <div className="flex items-center gap-2.5 rounded-lg border border-[#FFF] backdrop-blur md:ml-4 md:mr-4 sm:p-8 p-4">
        <div className="flex flex-col gap-5 w-full">
          <div className="flex flex-row items-center">
            <Image
              src="https://elchemy-website.s3.ap-south-1.amazonaws.com/products/icons/1709289729.svg"
              width={64}
              height={72}
              className="relative h-8 sm:h-14 md:h-16 ml-[-1rem]  sm:ml-[0] mb-2"
              alt="brand-logo"
            />
            <h1 className="text-white text-[1.8rem] font-mukta font-light sm:w-[70%] sm:text-[2.2rem]">
              Contact Us
            </h1>
          </div>

          {isSuccess == false ? (
            <>
              {isNorthAmericaAds ? (
                <>
                  <h2 className="text-white text-[20px] leading-[120%] font-mukta font-light sm:text-[38px] sm:leading-[120%]">
                    Your Trusted Partner for Personal Care Ingredient Sourcing
                  </h2>
                  <p className="text-white font-open-sans leading-6 font-normal">
                    Regulatory-ready ingredients. Fast fulfillment. Real-time
                    tracking
                  </p>
                </>
              ) : (
                <>
                  <h2 className="text-white text-[20px] leading-[120%] font-mukta font-light sm:text-[38px] sm:leading-[120%]">
                    Elchemy is the high-trust gateway to the Indian Chemical
                    Industry.{" "}
                  </h2>
                  <p className="text-white font-open-sans leading-6 font-normal">
                    Any question or remarks? Just write us a message!
                  </p>
                </>
              )}

              <Form
                handleSubmit={handleContactForm}
                initialValues={{
                  FirstName: "",
                  LastName: "",
                  EmailId: "",
                  MobilewithCountryCode: "",
                  Country: "",
                  Services: "",
                  info: "",
                  recaptcha: null,
                  timezone: "",
                  call_date: null,
                  call_time: null,
                }}
                validationSchema={schema}
              >
                {(formik) => {
                  const {
                    values,
                    errors,
                    handleChange,
                    setFieldValue,
                    handleBlur,
                    resetForm,
                    touched,
                  } = formik;
                  return (
                    <Grid container spacing={2}>
                      <div className=""></div>
                      <Grid item xs={24} sm={12} md={6} lg={6}>
                        <InputLabel
                          htmlFor="FirstName"
                          sx={{
                            color: "#FFF",
                            marginBottom: "8px",
                            fontSize: "14px",
                            lineHeight: "21px",
                            fontFamily: "var(--font-open-sans), sans-serif",
                          }}
                        >
                          Name
                          <span className="text-red ml-1">*</span>
                        </InputLabel>
                        <TextField
                          placeholder="First Name"
                          sx={{
                            width: "100%",
                            paddingTop: "0px",
                            borderRadius: "6px",
                            input: {
                              height: "13px",
                              fontWeight: "normal",
                              borderColor: "#B3B3B3",
                              borderRadius: "6px",
                              backgroundColor: "#FFF",
                            },
                            "& .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-colorPrimary":
                              {
                                borderRadius: {
                                  xs: "5px !important",
                                  lg: "5px !important",
                                },
                              },
                          }}
                          InputProps={{
                            style: {
                              borderRadius: "10px",
                              fontSize: "16px",
                              fontWeight: "normal",
                              fontFamily: "var(--font-mukta), sans-serif",
                            },
                          }}
                          name="FirstName"
                          onBlur={handleBlur}
                          value={values?.FirstName}
                          onChange={handleChange}
                          error={errors.FirstName}
                          helperText={
                            errors.FirstName && touched.FirstName
                              ? errors.FirstName
                              : ""
                          }
                        />
                      </Grid>
                      <Grid item xs={24} sm={12} md={6} lg={6}>
                        <InputLabel
                          htmlFor="LastName"
                          sx={{
                            color: "#FFF",
                            marginBottom: "8px",
                            fontSize: "14px",
                            lineHeight: "21px",
                            fontFamily: "var(--font-open-sans), sans-serif",
                          }}
                        >
                          Last Name
                          <span className="text-red ml-1">*</span>
                        </InputLabel>
                        <TextField
                          placeholder="Last Name"
                          sx={{
                            width: "100%",
                            paddingTop: "0px",
                            borderRadius: "6px",
                            input: {
                              height: "13px",
                              fontWeight: "normal",
                              borderColor: "#B3B3B3",
                              borderRadius: "6px",
                              backgroundColor: "#FFF",
                            },
                            "& .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-colorPrimary":
                              {
                                borderRadius: {
                                  xs: "5px !important",
                                  lg: "5px !important",
                                },
                              },
                          }}
                          InputProps={{
                            style: {
                              borderRadius: "10px",
                              fontSize: "16px",
                              fontWeight: "normal",
                              fontFamily: "var(--font-mukta), sans-serif",
                            },
                          }}
                          name="LastName"
                          onBlur={handleBlur}
                          value={values?.LastName}
                          onChange={handleChange}
                          error={errors.LastName}
                          helperText={
                            errors.LastName && touched.LastName
                              ? errors.LastName
                              : ""
                          }
                        />
                      </Grid>
                      <Grid item xs={24} sm={12} md={6} lg={6}>
                        <InputLabel
                          htmlFor="EmailId"
                          sx={{
                            color: "#FFF",
                            marginBottom: "8px",
                            fontSize: "14px",
                            lineHeight: "21px",
                            fontFamily: "var(--font-open-sans), sans-serif",
                          }}
                        >
                          Email Id
                          <span className="text-red ml-1">*</span>
                        </InputLabel>
                        <TextField
                          placeholder="Email Id"
                          sx={{
                            width: "100%",
                            paddingTop: "0px",
                            borderRadius: "6px",
                            input: {
                              height: "13px",
                              fontWeight: "normal",
                              borderColor: "#B3B3B3",
                              borderRadius: "6px",
                              backgroundColor: "#FFF",
                            },
                            "& .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-colorPrimary":
                              {
                                borderRadius: {
                                  xs: "5px !important",
                                  lg: "5px !important",
                                },
                              },
                          }}
                          InputProps={{
                            style: {
                              borderRadius: "10px",
                              fontSize: "16px",
                              fontWeight: "normal",
                              fontFamily: "var(--font-mukta), sans-serif",
                            },
                          }}
                          name="EmailId"
                          onBlur={handleBlur}
                          value={values?.EmailId}
                          onChange={handleChange}
                          error={errors.EmailId}
                          helperText={
                            errors.EmailId && touched.EmailId
                              ? errors.EmailId
                              : ""
                          }
                        />
                      </Grid>

                      {pathname !== "/elchemy-tech" || "/" ? (
                        <Grid item xs={24} lg={6} sm={12} md={6}>
                          <InputLabel
                            htmlFor="Services"
                            id="demo-simple-select-helper"
                            sx={{
                              color: "#FFF",
                              marginBottom: "8px",
                              fontFamily: "var(--font-open-sans), sans-serif",
                              fontSize: "14px",
                              lineHeight: "21px",
                            }}
                          >
                            Services looking for
                            {/* <span className="text-red ml-1">*</span> */}
                          </InputLabel>
                          <div>
                            <Select
                              labelId="demo-simple-select-helper-label"
                              id="demo-simple-select-helper"
                              value={values?.Services}
                              onChange={(event) => {
                                setFieldValue("Services", event.target.value);
                              }}
                              onBlur={handleBlur}
                              inputProps={{
                                fontSize: "18px",
                              }}
                              displayEmpty
                              renderValue={
                                values?.Services
                                  ? undefined
                                  : () => (
                                      <span
                                        style={{
                                          color: "#808080",
                                          fontStyle: "normal",
                                        }}
                                      >
                                        {" "}
                                        Select Service
                                      </span>
                                    )
                              }
                              sx={{
                                width: "100%",
                                height: "46px",
                                backgroundColor: "#FFF",
                                borderRadius: "10px",
                                ".MuiSelect-select": {
                                  color: "black",
                                },
                                ".MuiOutlinedInput-notchedOutline": {
                                  border: "1px solid #B3B3B3",
                                },
                                "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                                  {
                                    border: "1px solid #B3B3B3",
                                  },
                                "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                  {
                                    border: "1px solid #B3B3B3",
                                  },
                              }}
                              name="Services"
                              error={errors.Services && touched.Services}
                              helperText={
                                errors.Services && touched.Services
                                  ? errors.Services
                                  : ""
                              }
                            >
                              <MenuItem value={""}>
                                <em
                                  style={{
                                    color: "#FFF",
                                    fontStyle: "normal",
                                    fontSize: "16px",
                                  }}
                                >
                                  Select service
                                </em>
                              </MenuItem>
                              {options.map((items) => (
                                <MenuItem
                                  value={items?.label}
                                  key={items?.label}
                                >
                                  {items?.label}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors.Services && touched.Services ? (
                              <p className="text-[red]">{errors.Services}</p>
                            ) : (
                              ""
                            )}
                          </div>
                        </Grid>
                      ) : null}

                      <Grid item xs={24} sm={12} md={6} lg={6}>
                        <InputLabel
                          htmlFor="MobilewithCountryCode"
                          sx={{
                            color: "#FFF",
                            marginBottom: "8px",
                            fontSize: "14px",
                            lineHeight: "21px",
                            fontFamily: "var(--font-open-sans), sans-serif",
                          }}
                        >
                          Phone no
                          {/* <span className="text-red ml-1">*</span> */}
                        </InputLabel>
                        <div
                          className={`h-[46px] ${styles.phoneInputWrapper}`}
                        >
                          <PhoneInput
                            country={"us"}
                            enableSearch={true}
                            name="MobilewithCountryCode"
                            value={values?.MobilewithCountryCode}
                            onChange={(value, data) => {
                              setFieldValue("MobilewithCountryCode", value);
                            }}
                            onBlur={handleBlur}
                            error={
                              errors.MobilewithCountryCode &&
                              touched.MobilewithCountryCode
                            }
                          />
                          {errors.MobilewithCountryCode &&
                          touched.MobilewithCountryCode ? (
                            <p className="text-[red] text-[12px] mt-1">
                              {errors.MobilewithCountryCode}
                            </p>
                          ) : (
                            ""
                          )}
                        </div>
                      </Grid>

                      <Grid item xs={24} lg={6} sm={12} md={6}>
                        <InputLabel
                          htmlFor="Country"
                          id="demo-simple-select-helper"
                          sx={{
                            color: "#FFF",
                            marginBottom: "8px",
                            fontFamily: "var(--font-open-sans), sans-serif",
                            fontSize: "14px",
                            lineHeight: "21px",
                          }}
                        >
                          Country
                          {/* <span className="text-red ml-1">*</span> */}
                        </InputLabel>
                        <div>
                          <Select
                            labelId="demo-simple-select-helper-label"
                            id="demo-simple-select-helper"
                            value={values?.Country}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: "200px",
                                },
                              },
                            }}
                            slotProps={{
                              popup: {
                                disablePortal: true,
                              },
                            }}
                            onChange={(event) => {
                              setFieldValue("Country", event.target.value);
                            }}
                            onBlur={handleBlur}
                            inputProps={{
                              fontSize: "18px",
                            }}
                            displayEmpty
                            renderValue={
                              values?.Country
                                ? undefined
                                : () => (
                                    <span
                                      style={{
                                        color: "#808080",
                                        fontStyle: "normal",
                                      }}
                                    >
                                      {" "}
                                      Select Country
                                    </span>
                                  )
                            }
                            sx={{
                              width: "100%",
                              height: "46px",
                              backgroundColor: "#FFF",
                              borderRadius: "10px",

                              ".MuiSelect-select": {
                                color: "black",
                              },
                              ".MuiOutlinedInput-notchedOutline": {
                                border: "1px solid #B3B3B3",
                              },
                              "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                                {
                                  border: "1px solid #B3B3B3",
                                },
                              "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  border: "1px solid #B3B3B3",
                                },
                            }}
                            name="Country"
                            error={errors.Country && touched.Country}
                            helperText={
                              errors.Country && touched.Country
                                ? errors.Country
                                : ""
                            }
                          >
                            <MenuItem value={""}>
                              <em
                                style={{
                                  color: "#FFF",
                                  fontStyle: "normal",
                                  fontSize: "16px",
                                }}
                              >
                                Select Country
                              </em>
                            </MenuItem>
                            {countryOptions.map((items) => (
                              <MenuItem value={items?.name} key={items?.id}>
                                {items?.name}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.Country && touched.Country ? (
                            <p className="text-[red]">{errors.Country}</p>
                          ) : (
                            ""
                          )}
                        </div>
                      </Grid>

                      <Grid item xs={12}>
                        <InputLabel
                          htmlFor="Informations"
                          sx={{
                            color: "#FFF",
                            marginBottom: "8px",
                            fontSize: "14px",
                            lineHeight: "21px",
                            fontFamily: "var(--font-open-sans), sans-serif",
                          }}
                        >
                          Additional information that will help us connect
                          better {/* <span className="* text-[red]">*</span> */}
                        </InputLabel>
                        <TextField
                          sx={{
                            width: "100%",
                            borderRadius: "6px",
                            borderColor: "#B3B3B3",
                            backgroundColor: "#FFF",
                            input: {
                              fontFamily: "var(--font-open-sans), sans-serif",
                              fontSize: "14px",
                              fontWeight: "normal",
                              backgroundColor: "#FFF",
                              color: "#FFF",
                              lineHeight: "21px",
                            },
                          }}
                          InputProps={{
                            style: {
                              borderRadius: "6px",
                              fontSize: "14px",
                              fontWeight: "normal",
                              fontFamily: "var(--font-open-sans), sans-serif",
                            },
                          }}
                          placeholder="Type here..."
                          name="info"
                          onBlur={handleBlur}
                          multiline
                          rows={3}
                          value={values?.info}
                          onChange={handleChange}
                          error={errors.info && touched.info}
                          helperText={
                            errors.info && touched.info ? errors.info : ""
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <div className="text-white">
                          <TermsConditionBtn
                            checkedPolicy={checkedPolicy}
                            isChecked={isChecked}
                          />
                        </div>
                      </Grid>
                      <Grid item xs={12} md={12} lg={12}>
                        <ReCAPTCHA
                          style={{
                            transform: " scale(0.8)",
                            transformOrigin: "0 0",
                          }}
                          className="recaptcha"
                          name="recaptcha"
                          isChecked={isChecked}
                          value={values?.recaptcha}
                          sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
                          onChange={(value) => {
                            setFieldValue("recaptcha", value);
                          }}
                          size="normal"
                        />
                        {errors.recaptcha && touched.recaptcha ? (
                          <p
                            style={{
                              fontSize: "12px",
                            }}
                            className="text-[#d32f2f]"
                          >
                            {errors.recaptcha}
                          </p>
                        ) : (
                          ""
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <button
                          type="submit"
                          className={`w-[163px] text-[#FFFFFF] cursor-pointer h-[48px] rounded-[4px] bg-[#ef4136] 
                    font-semibold text-[16px] disabled:bg-[grey]`}
                          disabled={
                            !values?.EmailId ||
                            !values?.FirstName ||
                            !values?.LastName ||
                            !values?.recaptcha ||
                            !isChecked ||
                            isLoading
                          }
                        >
                          {isLoading ? "Submitting" : "Submit"}
                        </button>
                      </Grid>
                    </Grid>
                  );
                }}
              </Form>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <div className="relative w-30 h-30 mb-6">
                <Image
                  src={getCDNImageUrl("contactUsSuccessIcon.svg")}
                  alt="Success"
                  width={120}
                  height={120}
                  className="object-contain"
                />
              </div>
              <p className="text-white leading-[120%] font-mukta font-light sm:text-[24px]">
                Your request has been sent successfully! We will get back to you
                shortly.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ContactUsForm;
const today = new Date();
today.setHours(0, 0, 0, 0);
const schema = Yup.object().shape({
  FirstName: Yup.string().trim().required("First Name is required"),
  LastName: Yup.string().trim().required("Last Name is required"),
  EmailId: Yup.string()
    .matches(
      /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
      "Enter a valid email address"
    )
    .required("Email is required"),
  Country: Yup.string().trim(),
  MobilewithCountryCode: Yup.string().typeError(
    "That doesn't look like a phone number"
  ),
  Services: Yup.string().trim(),
  recaptcha: Yup.string().required("Please enter a recapatcha"),
  info: Yup.string().max(500, "Information exceeds 500 characters"),
});
